import { type FC, type MouseEventHandler, type PropsWithChildren, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import { selectInput } from '@core/reducers/inputsSlice';
import { selectTestResultById } from '@core/reducers/monarchSlice';
import { School } from '@core/ts/results';

import SNHUCardBadge from './SNHUCardBadge';
import SNHUCardDescriptionCta from './SNHUCardDescriptionCta';
import SNHUCardDescription from './SNHUDescription';

export type SnhuValuePropsTestEditableAsset = {
  heading?: string;
  valueProps?: { label?: string; text?: string }[];
  badges?: Record<string, string>;
  callout?: string;
};

type Props = {
  showDescription: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  viewCorrelationId: string;
  position?: number;
  school: School;
  isCoveredByElement: boolean;
} & PropsWithChildren;

type ReturnType = {
  SNHUCardDescription: FC<PropsWithChildren>;
  SNHUCardDescriptionCta: FC<PropsWithChildren>;
  SNHUCardBadge: FC<PropsWithChildren>;
  isTestEnabled: boolean;
};

/**
 * Hook to handle the PsychCard test
 * @param {Props} props
 * @returns {ReturnType} return the PsychCardDescription and PsychCardDescriptionCta HOCs
 */
const useSNHUCardTest = ({ school, showDescription, ...otherProps }: Props): ReturnType => {
  const descriptionRef = useRef<HTMLDivElement>(null);

  const flags = useFeatureFlags();

  const asset = flags?.snhuValuePropsTest;

  const importantFactors: string[] = useSelector((state) => selectInput(state, 'importantFactors'));

  // find the closest important factor we're interested in showing.
  const topFactorText = asset?.badges?.[importantFactors?.find((factor) => !!asset?.badges?.[factor]) as string];

  const isTestEnabled = !!asset && school?.id === 142 && !!topFactorText;

  // check if the description overflows the container. The fixed height in the styles is 56px.
  const doesDescriptionOverflow = Number(descriptionRef.current?.scrollHeight) > 56;

  // return the test components only if the test is enabled
  return {
    isTestEnabled,
    SNHUCardBadge: ({ children }: PropsWithChildren) =>
      isTestEnabled ? <SNHUCardBadge text={topFactorText} /> : <>{children}</>,
    SNHUCardDescription: ({ children }: PropsWithChildren) =>
      isTestEnabled ? (
        <SNHUCardDescription
          {...otherProps}
          valueProps={asset?.valueProps}
          // we need to pass the ref to the description component to check if the description overflows the container
          descriptionRef={descriptionRef}
          header={asset?.heading}
          showDescription={showDescription || !doesDescriptionOverflow}
        />
      ) : (
        <>{children}</>
      ),
    SNHUCardDescriptionCta: ({ children }: PropsWithChildren) =>
      isTestEnabled ? (
        <SNHUCardDescriptionCta
          doesDescriptionOverflow={doesDescriptionOverflow}
          {...otherProps}
          showDescription={showDescription}
        />
      ) : (
        <>{children}</>
      ),
  };
};

export default useSNHUCardTest;
